//  ----------------------------------------------------------------------------
//  Dependencies
//  ----------------------------------------------------------------------------
import { invert, lighten } from 'polished'
import React, { useRef, useState } from 'react'
import { connect } from 'react-redux'
import styled from 'styled-components'
//  -- Components ---------------------
import { Assignment, Extension, Delete, ContentCopy } from '@mui/icons-material'
import { Modal, Button, IconButton, Tooltip } from '@mui/material'
import { Drawer, Row, Col, message } from 'antd'

//  -- Constants ----------------------
import COLOR from 'utils/constants/color'
import { useDispatch } from 'react-redux';
//  -- Features -----------------------

//  -- Thunks -------------------------
import { get , has } from 'lodash'
import { useEffect } from 'react'
import AssignmentForm from './AssignmentForm'
import Loading from 'components/Loading'
import { handleAdminDeleteActivities, handleGetAssignmentAssignStatus } from "store/activities/thunks";
import { handleAdminDeleteQuizzes } from "store/quizzes/thunks";
import SpinLoader from 'components/Loader/SpinLoader'
import CloneActivityToPageModal from 'components/CloneActivityToPageModal'
import GraphicAssignmentForm from './PageToolbarModals/GraphicOrganizer/Form'
import { graphic_data } from 'utils/constants/graphic_org'
import * as Sentry from '@sentry/react'
import SentryWrap from 'components/Sentry';
import { deleteDoc, doc } from 'firebase/firestore'
import { useFirebase } from 'containers/FirebaseContext'
import { useCollectionActivity } from 'utils/methods/firebaseActivity'
import { useHasPermission } from 'utils/permissions'
import { useUI } from 'store/UI/selectors'
import { assignmentDraftHasUserContent } from 'features/forms/assignment/studentMatchMaking/helper'
//  ----------------------------------------------------------------------------
//  Styles
//  ----------------------------------------------------------------------------
const ASSIGNMENT_BG = ['#cfd5d045', '#dbe4f0', '#58aedb4f', '#dda4e74d', '#3fdd6645', '#e9bf864d'];
const Column = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: 4px;
`

const ModalContent = styled.div`
  align-items: center;
  background-color: ${COLOR.white};
  border-radius: 3px;
  display: inline-flex;
  flex-direction: column;
  left: 50%;
  max-width: 330px;
  padding: 16px;
  position: absolute;
  text-align: center;
  top: 50%;
  transform: translate(-50%, -50%);
`

const RowItem = styled.div`
  align-items: ${(p) => p.align || 'center'};
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  margin-bottom: 16px;
  width: 100%;

  &:last-child {
    align-items: flex-end;
    justify-content: space-between;
    margin-bottom: 0px;
  }
`
const ModalRow = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  justify-content: center;
  margin-top: 16px;
  width: 100%;
`

const Text = styled.span`
  color: ${COLOR.black500};
  font-size: 11px;
  margin-top: 2px;
  text-align: left;
`
const TextSubHeader = styled.span`
  color: ${COLOR.black500};
  font-size: 11px;
  margin-top: 2px;
`

const TextHeader = styled.span`
  font-size: 11px;
  margin-top: 2px;
  text-align: left;
`

const TextLarge = styled(Text)`
  color: #3d5f8f;
  font-size: 14px;
  font-weight: 600;
  margin-right: 4px;
`

const Title = styled.span`
  font-size: 14px;
  font-weight: 600;
  line-height: 18px;
  text-align: left;
  word-break: break-word;
`

const Wrapper = styled.button`
  background-color: ${(p) =>
        p.bgColor && !ASSIGNMENT_BG.includes(p.bgColor) ? p.bgColor : lighten(0.55, '#3D5F8F')};
  border: 2px dashed
    ${(p) =>
        p.borderColor
            ? lighten(0.2, invert(p.bgColor))
            : lighten(0.3, '#3D5F8F')};
  border-radius: 4px;
  color: #3d5f8f;
  cursor: ${(p) => (p.disabled ? 'no-drop' : 'pointer')};
  font-size: 14px;
  font-weight: 400;
  margin-bottom: 16px;
  outline: none;
  padding: 12px 12px 10px;
  padding-left: ${(p) => (p.hasLabel ? '50px' : '12px')};
  transition: all 300ms ease;
  width: 100%;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 40px;
    background: ${(p) =>
      p.isUnitExam ? '#ffa947' : p.isExam ? '#0F2C70' : p.isGeneratedTest ? '#cffafe' : 'transparent'};
    border-radius: 4px 0 0 4px;
    z-index: 0;
  }

  &:hover {
    background-color: ${(p) =>
        p.hoverColor ? p.hoverColor : lighten(0.5, '#3D5F8F')};
    
    &::before {
      background: ${(p) =>
        p.isUnitExam ? '#ffa947' : p.isExam ? '#0F2C70' : p.isGeneratedTest ? '#cffafe' : 'transparent'};
    }
  }
`
const BookDescRow = styled(RowItem)`
  font-size: 10px;
  padding-left: 30px;
  margin-top: -15px;
`
const Columns = styled(Col)`
    display: flex;
    align-items: flex-end;
    justify-content: flex-end;
`;


const Assignments = ({ assignment, pageSrc, bookId, pageId, draft = false, draftId = null, setDraftId = () => {}, onDraftDeleted = () => {}, onAdminAssignmentRefetch = () => {}, onDraftSaved = () => {}, currentUser }) => {
    const [drawer, toggleDrawer] = useState(false)
    const [openedFromDraftCard, setOpenedFromDraftCard] = useState(false)
    const colorCode = get(assignment, 'colorCode')
    const bookDescription = get(assignment, 'bookDescription')
    const [deleteToggle, setDeleteToggle] = useState(false)
    const [deleting, setDeleting] = useState(false)
    const dispatch = useDispatch();
    const { firestore } = useFirebase();
    const { createBlankDraftDoc, createDraftActivity } = useCollectionActivity();
    const hasAssignment = useHasPermission('book_tools.assignment');
    const [openViewAssigned, setOpenViewAssigned] = useState({data: {}, open:false});
    const [loading, setLoading] = useState(false);
    const [activityDrawer, setActivityDrawer] = useState(false)
    const [cloneToPageOpen, setCloneToPageOpen] = useState(false)
    const isUnitExam = get(assignment, 'is_unit_exam');
    const is_exam = !!assignment?.is_exam;
    const isGeneratedTest = Boolean(get(assignment, 'generated_test'));
    const assignmentKindLabel = isUnitExam ? 'unit examen' : is_exam ? 'examen' : get(assignment, 'typeId', 'assignment');
    const hadPublishedActivity = assignment?.id != null && assignment?.id !== '';
    const recoveryInFlightRef = useRef(new Set());
    const recoveredKeysRef = useRef(new Set());

    useEffect(() => {
      const recoverRefreshDraft = async () => {
        const activityKey = assignment?.id || assignment?.docId || 'new';
        const storageKey = `assignment-form-refresh-backup-${bookId}-${pageId}-${activityKey}`;
        if (recoveredKeysRef.current.has(storageKey) || recoveryInFlightRef.current.has(storageKey)) {
          return;
        }
        const pendingDraftRaw = window.sessionStorage.getItem(storageKey);
        if (!pendingDraftRaw) return;
        recoveryInFlightRef.current.add(storageKey);
        window.sessionStorage.removeItem(storageKey);
        try {
          const parsed = JSON.parse(pendingDraftRaw);
          const draftData = parsed?.draftData || parsed;
          if (!assignmentDraftHasUserContent(draftData)) {
            recoveredKeysRef.current.add(storageKey);
            return;
          }
          let recoveryDraftId = parsed?.draftId || assignment?.docId || draftId;
          if (!recoveryDraftId) {
            recoveryDraftId = await createBlankDraftDoc({ ...assignment });
          }
          if (!recoveryDraftId) return;
          await createDraftActivity(recoveryDraftId, draftData);
          setDraftId(recoveryDraftId);
          onDraftSaved({
            docId: recoveryDraftId,
            ...draftData,
            questions: Array.isArray(draftData.questions) ? draftData.questions : (typeof draftData.questions === 'string' ? JSON.parse(draftData.questions || '[]') : []),
            draft: true
          });
          recoveredKeysRef.current.add(storageKey);
        } catch (error) {
          Sentry.captureException(error);
        } finally {
          recoveryInFlightRef.current.delete(storageKey);
        }
      };
      recoverRefreshDraft();
    }, [assignment?.id, assignment?.docId, bookId, pageId, draftId]);

    const onCloseDrawer = (savedDraft) => {
        if (savedDraft?.docId) {
            onDraftSaved(savedDraft);
        }
        toggleDrawer(false)
        setOpenedFromDraftCard(false)
        setDraftId(null);
        setTimeout(() => onDraftDeleted(), 1500);
    }

    const handleDeleteDraft = async (draftIdToDelete) => {
      const idToDelete = draftIdToDelete || (draft ? assignment?.docId : null) || draftId; 
      if (!idToDelete) {
        console.error('No draftId available for deletion');
        message.error('Cannot delete draft: draft ID is missing.');
        return;
      }
      
      setDeleting(true)
      try {
        const docRef = doc(firestore, import.meta.env.VITE_APP_STAGE?.trim() === 'prod' ? 'draft_activity_prod' : 'draft_activity', idToDelete);
        await deleteDoc(docRef);
        if (draftId === idToDelete) {
          setDraftId(null);
        }
        setDeleteToggle(false)
        message.success('Draft deleted successfully');
        onDraftDeleted();
      } catch (error) {
        console.error('Error deleting draft:', error);
        Sentry.captureException(error);
        message.error('Failed to delete draft. Please try again.');
      } finally {
        setDeleting(false);
      }
    };

    const createBlankDraft = async () => {
      const draftData = {
        ...assignment
      }

      try {
        const newDraftId = await createBlankDraftDoc(draftData);
        return newDraftId;
      } catch (e) {
        console.error('Error creating blank draft:', e);
        return null;
      }
    };

    const onDelete = async () => {
        if (draft) {
            // need to add fnction for delete draft
            const draftIdToDelete = assignment?.docId || draftId;
            if (!draftIdToDelete) {
                message.error('Cannot delete draft: draft ID is missing.');
                setDeleteToggle(false);
                return;
            }
            await handleDeleteDraft(draftIdToDelete);
            return;
        }
        if (has(assignment, "id")) {
            setDeleting(true)
            if (assignment.typeId == "activity") {
                await dispatch(handleAdminDeleteActivities(assignment.id))

            } else {
                await dispatch(handleAdminDeleteQuizzes(assignment.id))
            }
            setDeleting(false)
            setDeleteToggle(false)
        }
    }
    const onCreateOrUpdate = () => {
        toggleDrawer(false)
    }

    const onClose = () => {
        setOpenViewAssigned((prev) => ({...prev, data: {}, open: false }))
    }
    const getAssignmentData = async(id) => {
       try {
        if(!id) return;
        setLoading(true);
        const data = await handleGetAssignmentAssignStatus(id)
        if(data && has(data, "data") && has(data.data, "submission_count")){
            setOpenViewAssigned((prev) => ({...prev, open: true , data:data}));
        }
       } catch (error) {
        Sentry.captureException(`290 Error in getAssignmentData: ${error}`);
       } finally {
           setLoading(false);
       }
    }

    const {openEngageModal=false} = useUI();
    return (
        <SentryWrap>
            <CloneActivityToPageModal
                open={cloneToPageOpen}
                onClose={() => setCloneToPageOpen(false)}
                adminMode
                assignment={
                    assignment?.id
                        ? {
                              id: assignment.id,
                              chapter: assignment.chapter ?? bookId,
                              bookId: assignment.bookId ?? bookId,
                              page: assignment.page ?? pageId,
                              type: assignment.type,
                              typeId: assignment.typeId,
                              displayName: assignment.displayName,
                          }
                        : null
                }
                onSuccess={(destChapter) => {
                    onAdminAssignmentRefetch(destChapter);
                }}
            />
            <OpenViewAssignedModel data={openViewAssigned} onClose={onClose} />
            <SpinLoader dataloader={loading} />
            <Modal open={deleteToggle} onClose={() => setDeleteToggle(false)}>
                <ModalContent>
                    <TextHeader>
                        <strong>
                            {draft
                                ? `Are you sure you want to delete this draft ${assignmentKindLabel}?`
                                : `Are you sure you want to delete this ${assignmentKindLabel}?`}
                        </strong>
                    </TextHeader>
                    <TextSubHeader>
                        <strong>{`Deleting this activity will also remove all related submissions and assignments`}</strong>
                    </TextSubHeader>
                    <ModalRow>
                        <Button
                            style={{ marginRight: '4px' }}
                            onClick={() => setDeleteToggle(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            color="secondary"
                            variant="contained"
                            onClick={onDelete}
                            className={`${deleting ? 'disabled-button' : ''}`}
                        >
                            {deleting ? <Loading size={20} white /> : 'Delete'}
                        </Button>
                    </ModalRow>
                </ModalContent>
            </Modal>
            {drawer && (
                <AssignmentForm
                    pageSrc={pageSrc}
                    data={assignment}
                    bookId={bookId}
                    pageId={pageId}
                    openedFromDraftCard={openedFromDraftCard}
                    onCreateOrUpdate={onCreateOrUpdate}
                    type={get(assignment, 'typeId', '')}
                    draftId={draftId}
                    setDraftId={setDraftId}
                    onClose={onCloseDrawer}
                    open={drawer}
                    isDisableDraft={openEngageModal ? true : false}
                    configEngageMent={{
                        isFrom: assignment?.config_assignment?.isFrom || '',
                        sequence_order: assignment?.config_assignment?.sequence_order || '',
                        engage_id: assignment?.config_assignment?.engage_id || '',
                      }}
                />
            )}
            <Drawer
                height='100%'
                destroyOnHidden
                onClose={()=> setActivityDrawer(false)}
                placement='top'
                open={activityDrawer}
                key='activityNew'
                className='assignments-drawer'
            >
                <>
                    <GraphicAssignmentForm
                        pageId={pageId}
                        bookId={bookId}
                        pageSrc={graphic_data.find((v) => v.pageId == assignment.assignment_page)?.src}
                        data={assignment}
                        assignment={assignment}
                        onClose={() => {
                            setActivityDrawer(false)
                        }}
                    />
                </>
            </Drawer>
            <Wrapper 
                bgColor={colorCode} 
                borderColor={null} 
                hoverColor={colorCode} 
                hasLabel={isUnitExam || is_exam || isGeneratedTest}
                isUnitExam={isUnitExam}
                isExam={is_exam}
                isGeneratedTest={isGeneratedTest}
                className={
                    isUnitExam
                        ? 'addUnitCstm'
                        : is_exam
                          ? 'addAssignExamCstm'
                          : isGeneratedTest
                            ? 'addGeneratedTestCstm'
                            : ''
                }
            >
                {(isUnitExam || is_exam || isGeneratedTest) && (
                    <div className='cardlabelSet' 
                        style={{ 
                            position: 'absolute',
                            left: '0',
                            top: '0',
                            bottom: '0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '40px',
                            color: isUnitExam ? '#43969C' : is_exam ? 'white' : isGeneratedTest ? '#0e7490' : '#43969C',
                            zIndex: 1
                        }}
                    >
                        {isUnitExam ? 
                            <span className="assignmentLabel" style={{ color: isUnitExam ? '#43969C' : is_exam ? 'white' : '#43969C'}}>EXAMEN DE UNIDAD</span> 
                            : is_exam ? 
                            <span className="text-white-10" style={{ color: isUnitExam ? '#43969C' : is_exam ? 'white' : '#43969C'}}>PRUEBA</span> 
                            : isGeneratedTest ? (
                            <span className="assignmentLabel" style={{ color: '#0e7490' }}>{isGeneratedTest?"ACTIVIDAD GENERADA":"ACTIVIDAD"}</span>
                            ) : null}
                    </div>
                )}
                <RowItem align="flex-start" style={{ position: 'relative', zIndex: 1 }}>
                    {assignment?.typeId === 'activity' ? (
                        <Extension style={{ height: '18px', marginRight: '4px' }} />
                    ) : (
                        <Assignment style={{ height: '18px', marginRight: '4px' }} />
                    )}
                    <Title>{get(assignment, 'displayName', '')}</Title>
                </RowItem>
                {bookDescription ? (
                    <BookDescRow>
                        <strong>Book</strong>: {bookDescription}
                    </BookDescRow>
                ) : null}
                <RowItem>
                    <Column>
                        <Text>
                            Type : <strong>{assignmentKindLabel} {draft ? (hadPublishedActivity ? '(Draft-Published)' : '(Draft-Unpublished)') : ''}</strong>
                        </Text>
                    </Column>
                </RowItem>
                <Row>
                    <Columns span={16} style={{ justifyContent: "flex-start" }}>
                        <Text>
                            Number of questions:{' '}
                            <strong style={{ color: COLOR.black300 }}>
                                {get(assignment, 'questions.length')}
                            </strong>
                        </Text>
                    </Columns>
                </Row>
                <div className='w-full flex items-center gap-10px justify-end'>
                    <div className='flex items-center'>
                        <>
                            <TextLarge
                                onClick={async() => {
                                    if (!hasAssignment) {
                                        message.warning('You do not have permission to access this feature.');
                                        return;
                                    }
                                    if(assignment.activity_type=='graphic_organizer'){
                                        setActivityDrawer(true)
                                    }else{
                                        setOpenedFromDraftCard(!!draft)
                                        if (assignment?.id) {
                                            const newDraftId = await createBlankDraft();
                                            if (newDraftId) setDraftId(newDraftId);
                                        } else {
                                            setDraftId(assignment?.docId || null);
                                        }
                                        toggleDrawer(true)
                                    }
                                }}
                            >
                                Edit
                            </TextLarge>
                            {!draft && (
                                <>
                                    <TextLarge>
                                        |
                                    </TextLarge>
                                    <TextLarge
                                        onClick={() => {
                                            if (!hasAssignment) {
                                                message.warning('You do not have permission to access this feature.');
                                                return;
                                            }
                                            getAssignmentData(assignment?.id)
                                        }}
                                    >
                                        View status
                                    </TextLarge>
                                    <TextLarge>
                                        |
                                    </TextLarge>
                                </>
                            )}
                            {get(currentUser, 'role') === 'owe_admin' && !draft && assignment?.id ? (
                                <>
                                    <TextLarge>|</TextLarge>
                                    <TextLarge
                                        onClick={() => {
                                            if (!hasAssignment) {
                                                message.warning('You do not have permission to access this feature.');
                                                return;
                                            }
                                            setCloneToPageOpen(true);
                                        }}
                                    >
                                        <ContentCopy style={{ height: 14, verticalAlign: 'middle', marginRight: 2 }} />
                                    </TextLarge>
                                </>
                            ) : null}
                            <Tooltip title={draft ? `draft ${assignmentKindLabel}` : assignmentKindLabel}>
                                <IconButton style={{ padding: 0 }} color="secondary" onClick={() => {
                                    if (!hasAssignment) {
                                        message.warning('You do not have permission to access this feature.');
                                        return;
                                    }
                                    setDeleteToggle(true);
                                }}>
                                    <Delete color="secondary" />
                                </IconButton>
                            </Tooltip>
                        </>
                    </div>
               
                </div>
            </Wrapper>
        </SentryWrap>
    )
}


//  ----------------------------------------------------------------------------
//  Redux
//  ----------------------------------------------------------------------------
const mapStateToProps = (state) => ({
    currentClass: state.currentClass,
    booksAllList: state.booksAllList,
    currentUser: state.currentUser
})

export default connect(mapStateToProps, null)(Assignments)


const OpenViewAssignedModel = ({ data, onClose }) => {
  const responseData = data?.data?.data || {};
  return (
    <Modal open={data?.open} onClose={onClose}>
      <ModalContent style={{ minWidth: 300 }}>
        <div>
          <Rows> Assigned :- {responseData?.assigned_count||0} </Rows>
          <Rows> Submissions :- {responseData?.submission_count||0} </Rows>
          <Row> {""} </Row>
        </div>
        <ModalRow>
          <Button
            color="primary"
            variant="contained"
            style={{ marginRight: "4px" }}
            onClick={() => onClose()}
          >
            ok
          </Button>
        </ModalRow>
      </ModalContent>
    </Modal>
  );
};
 

const Rows = styled.div`
    display: flex;
    flex-direction: row;
    margin-bottom: 16px;
    width: 100%;
    align-items: center;
    justify-content:center;
`;